import { useState, type FormEvent } from 'react';
import { Gift, Library, ListChecks, Search, type LucideIcon } from 'lucide-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthApiError, emailAuth } from '@/api/auth/emailAuth';
import { useAuth } from '@/auth/authContext';
import { useT } from '@/i18n/locale';

import { AuthLayout } from './AuthLayout';
import {
  AuthField,
  AuthSwitch,
  FormAlert,
  OrDivider,
  PasswordField,
  PasswordRequirements,
  SubmitButton,
} from './fields';
import { focusField, friendlyAuthError } from './form';
import { sanitizeNext } from './next';
import { meetsPasswordPolicy } from './password';
import { SocialButtons } from './SocialButtons';

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const PERKS: Array<{ icon: LucideIcon; key: string }> = [
  { icon: Gift, key: 'signup.perk.free' },
  { icon: Search, key: 'signup.perk.search' },
  { icon: Library, key: 'signup.perk.library' },
  { icon: ListChecks, key: 'signup.perk.episodes' },
];

/*
 * What the account gets you, before the form. The landing site's web CTAs land
 * here, and below lg there is no marquee — on a phone this list is the only
 * thing saying what ScreenAtlas is.
 */
function Perks() {
  const t = useT();
  return (
    <ul aria-label={t('signup.perksLabel')} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
      {PERKS.map(({ icon: Icon, key }) => (
        <li key={key} className="flex items-center gap-3 text-subhead text-ink">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft">
            <Icon className="h-4 w-4 text-accent-ink" aria-hidden="true" />
          </span>
          {t(key)}
        </li>
      ))}
    </ul>
  );
}

export function SignupScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = sanitizeNext(searchParams.get('next'));
  const t = useT();
  const { isAuthenticated } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const nextErrors: typeof errors = {};
    if (!EMAIL_RE.test(email.trim())) nextErrors.email = t('signup.emailValid');
    if (!meetsPasswordPolicy(password)) nextErrors.password = t('signup.weakPassword');
    setErrors(nextErrors);
    setFormError(null);
    if (Object.keys(nextErrors).length > 0) {
      focusField(nextErrors.email ? 'email' : 'password');
      return;
    }

    setBusy(true);
    try {
      await emailAuth.signUp(email.trim(), password);
      navigate(
        `/confirm?email=${encodeURIComponent(email.trim())}${
          next === '/' ? '' : `&next=${encodeURIComponent(next)}`
        }`,
      );
    } catch (error) {
      if (error instanceof AuthApiError && (error.status === 409 || /already exists|usernameexists/i.test(error.message))) {
        setErrors({ email: t('signup.emailExists') });
        return;
      }
      // The backend forwards Cognito's raw exception strings verbatim; the
      // app pattern-matches them into plain language (authApi.ts) — same here.
      if (error instanceof AuthApiError && /password did not conform|invalidpassword|password.*policy/i.test(error.message)) {
        setErrors({ password: t('signup.weakPassword') });
        return;
      }
      if (error instanceof AuthApiError && error.status === 0) {
        setFormError(t('login.error.network'));
        return;
      }
      setFormError(friendlyAuthError(error, t, 'login.error.unknown'));
    } finally {
      setBusy(false);
    }
  }

  // Landing CTAs point here; someone already signed in goes straight in.
  if (isAuthenticated) return <Navigate to={next} replace />;

  const loginTo = next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`;

  return (
    <AuthLayout title={t('signup.heading')} lede={t('signup.lede')}>
      <div className="flex flex-col gap-6">
        <Perks />
        <SocialButtons returnTo={next} />
        <OrDivider>{t('auth.orEmail')}</OrDivider>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          {formError ? <FormAlert>{formError}</FormAlert> : null}
          <AuthField
            id="email"
            label={t('login.email')}
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            error={errors.email}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
              setErrors((prev) => ({ ...prev, email: undefined }));
            }}
          />
          <PasswordField
            id="password"
            label={t('login.password')}
            autoComplete="new-password"
            value={password}
            error={errors.password}
            disabled={busy}
            onChange={(event) => {
              setPassword(event.target.value);
              setErrors((prev) => ({ ...prev, password: undefined }));
            }}
            hint={<PasswordRequirements value={password} />}
          />
          <SubmitButton busy={busy}>{t('signup.title')}</SubmitButton>
          <p className="text-caption text-ink-secondary">
            {t('signup.legalBefore')}{' '}
            <a href={t('signup.privacyHref')} className="text-ink underline underline-offset-4">
              {t('signup.legalLink')}
            </a>
            .
          </p>
        </form>
      </div>

      <div className="mt-8">
        <AuthSwitch prompt={t('signup.haveAccount')} to={loginTo} action={t('login.signIn')} />
      </div>
    </AuthLayout>
  );
}
